import React from "react";

import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import {
  CHART_CONTAINER_HEIGHT,
  STANDART_CHART_HEIGHT,
} from "../common/values";
import {
  IChartProps,
  SystemPerformanceResponseModel,
} from "../../system-performance/models/chart";
import StyledChart, {
  apexChartsTooltipStyles,
} from "../../../../../common/components/apexchart/StyledChart";

import CircularLoading from "../../../../../common/components/progress/CircularLoading";
import InfoIcon from "../../../../../common/components/icons/InfoIcon";
import SimplePaperWrapper from "../../../../../common/components/item/SimplePaperWrapper";
import StatisticsNoDataView from "../../system-performance/components/StatisticsNoDataView";
import SystemHealthTable from "./SystemHealthTable";
import { useLoaderProgress } from "../../../../../common/hooks/useProgressLoader";

type ChartTableContainerProps<T extends SystemPerformanceResponseModel> = {
  chartsData: IChartProps<T>;
  tooltipText?: string;
  showChartOnly?: boolean;
  labelText?: string;
  title: string;
  tableItemName: string;
  type: "line" | "bar";
  jobId?: string;
};

const ChartTableContainer = <T extends SystemPerformanceResponseModel>(
  props: ChartTableContainerProps<T>
) => {
  const {
    chartsData,
    title,
    tooltipText,
    labelText,
    tableItemName,
    type,
    showChartOnly = false,
    jobId,
  } = props;

  const theme = useTheme();

  const { showNoData, series, originalData, options, loading, refetching } =
    chartsData;

  const { progress, showLoader } = useLoaderProgress({
    jobId,
    loading,
    refetching,
  });

  let showChart = series && series?.length <= 8;

  if (showChartOnly) {
    showChart = true;
  }

  return (
    <SimplePaperWrapper
      sx={{
        padding: "1.5em",
        height: CHART_CONTAINER_HEIGHT,
        borderRadius: "8px",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          marginBottom: "1em",
          gap: "0.5em",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1em" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Typography variant="h5">{title}</Typography>

            {tooltipText && (
              <Tooltip title={tooltipText} arrow placement="right">
                <Box
                  sx={{ display: "flex", alignItems: "center" }}
                  component="span"
                >
                  <InfoIcon color={theme.palette.text.primary} />
                </Box>
              </Tooltip>
            )}
          </Box>

          {labelText && (
            <Typography
              sx={{
                color: theme => theme.palette.otherTextTertiary.main,
              }}
              variant="body2Regular"
            >
              {labelText}
            </Typography>
          )}
        </Box>
      </Box>

      {showLoader && <CircularLoading height="75%" percents={progress} />}

      {!loading &&
        !showNoData &&
        (showChart ? (
          <Box sx={apexChartsTooltipStyles}>
            <StyledChart
              options={options}
              series={series}
              height={STANDART_CHART_HEIGHT}
              type={type ?? "line"}
            />
          </Box>
        ) : (
          <Box>
            <SystemHealthTable
              data={originalData ?? []}
              loading={loading}
              title={title}
              tableItemName={tableItemName}
            />
          </Box>
        ))}

      {showNoData && <StatisticsNoDataView />}
    </SimplePaperWrapper>
  );
};

export default React.memo(ChartTableContainer);
